/**
 * @file     classifyLocation.ts
 * @purpose  Reverse-geocode coordinates via Mapbox Geocoding API and classify into Metro Vancouver, rest-of-BC, or outside-BC. Cached in localStorage to avoid repeat calls.
 */

import {
  GEO_CLASSIFY_STORAGE_KEY,
  isInMetroVancouverBbox,
} from './sharedGeolocation';

export type LocationKind = 'metro-vancouver' | 'bc-outside-metro' | 'outside-bc';

export type LocationClassification = {
  kind: LocationKind;
  region: string;        // e.g. "Kelowna", "Seattle", "Vancouver"
  placeName: string;     // e.g. "Kelowna, British Columbia"
  lng: number;
  lat: number;
  ts: number;            // timestamp, for cache expiry
};

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h, coords don't drift that fast

const METRO_VAN_PLACES = new Set([
  'vancouver', 'burnaby', 'richmond', 'surrey', 'coquitlam', 'port coquitlam',
  'port moody', 'new westminster', 'delta', 'north vancouver', 'west vancouver',
  'pitt meadows', 'maple ridge', 'langley', 'white rock', 'bowen island',
  'anmore', 'belcarra', 'lions bay',
]);

function readCache(lng: number, lat: number): LocationClassification | null {
  try {
    const raw = localStorage.getItem(GEO_CLASSIFY_STORAGE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as LocationClassification;
    if (Date.now() - cached.ts > CACHE_TTL_MS) return null;
    // Only use cache if coords are within ~1km of previous reading
    const dLng = Math.abs(cached.lng - lng);
    const dLat = Math.abs(cached.lat - lat);
    if (dLng > 0.01 || dLat > 0.01) return null;
    return cached;
  } catch {
    return null;
  }
}

function writeCache(result: LocationClassification): void {
  try {
    localStorage.setItem(GEO_CLASSIFY_STORAGE_KEY, JSON.stringify(result));
  } catch {
    // ignore
  }
}

type MapboxContextItem = { id: string; text: string; short_code?: string };
type MapboxFeature = {
  place_name: string;
  text: string;
  place_type: string[];
  context?: MapboxContextItem[];
};
type MapboxGeocodingResponse = { features: MapboxFeature[] };

export async function classifyLocation(
  lng: number,
  lat: number,
  token: string | undefined,
): Promise<LocationClassification> {
  const cached = readCache(lng, lat);
  if (cached) return cached;

  // Fallback classification when no token or fetch fails, use bbox only. Still useful.
  const bboxKind: LocationKind = isInMetroVancouverBbox(lng, lat)
    ? 'metro-vancouver'
    : 'outside-bc';

  if (!token) {
    return { kind: bboxKind, region: '', placeName: '', lng, lat, ts: Date.now() };
  }

  try {
    const url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json` +
      `?access_token=${encodeURIComponent(token)}&types=place,region,country&language=en&limit=1`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) throw new Error(`geocoding ${res.status}`);
    const data = (await res.json()) as MapboxGeocodingResponse;
    const feature = data.features?.[0];
    if (!feature) {
      return { kind: bboxKind, region: '', placeName: '', lng, lat, ts: Date.now() };
    }

    const context = feature.context ?? [];
    const region = context.find((c) => c.id.startsWith('region'));
    const country = context.find((c) => c.id.startsWith('country'));

    // Country check, ISO country short code is e.g. "ca", "us"
    const countryCode = country?.short_code?.toLowerCase();
    const isCanada = countryCode === 'ca' || feature.place_type.includes('country') && feature.text.toLowerCase() === 'canada';

    // Region check, BC short_code is "CA-BC"
    const regionShort = region?.short_code?.toLowerCase();
    const isBC = regionShort === 'ca-bc' || region?.text === 'British Columbia';

    // Place name, for place features the feature.text IS the city; otherwise pull from context
    const place = feature.place_type.includes('place')
      ? feature.text
      : context.find((c) => c.id.startsWith('place'))?.text ?? feature.text;
    const placeLower = place.toLowerCase();

    let kind: LocationKind;
    if (isBC && METRO_VAN_PLACES.has(placeLower)) {
      kind = 'metro-vancouver';
    } else if (isBC) {
      kind = 'bc-outside-metro';
    } else if (isCanada) {
      kind = 'outside-bc';
    } else {
      kind = 'outside-bc';
    }

    const result: LocationClassification = {
      kind,
      region: place,
      placeName: feature.place_name,
      lng,
      lat,
      ts: Date.now(),
    };
    writeCache(result);
    return result;
  } catch {
    return { kind: bboxKind, region: '', placeName: '', lng, lat, ts: Date.now() };
  }
}
